<script setup lang="ts">
import { computed, ref, type HTMLAttributes } from 'vue'
import { PopoverAnchor } from 'reka-ui'
import { Button, type ButtonVariants } from '@ds/primitives/button'
import { cn } from '@ds/lib/utils'
import { XIcon } from '@lucide/vue'
import { DSButton, DSPopover, DSPopoverContent, DSPopoverTrigger } from '@ds'

type Props = {
  /** A trigger for some state elsewhere (a filter, a sort, a view option) that
   * isn't visible on the button itself — e.g. "this filter isn't empty". */
  indicator?: boolean
  clearable?: boolean
  readonly?: boolean
  variant?: ButtonVariants['variant']
  size?: ButtonVariants['size']
  class?: HTMLAttributes['class']
}

const { indicator = false, variant, size, clearable = false, readonly = false, ...props } = defineProps<Props>()

const emit = defineEmits<{
  (e: 'click:clear'): void
}>()

defineOptions({
  inheritAttrs: false,
})

const open = ref(false)

const indicatorClasses = computed(() => {
  if (!clearable || readonly) return
  return [
    'flex items-center justify-center transition-all',
    !open.value && 'group-hover:size-4 group-hover:-top-1.25 group-hover:-right-1.25',
    !open.value && 'hover:size-4.5 hover:-top-1.5 hover:-right-1.5',
    open.value && 'size-4.5 -top-1.5 -right-1.5',
    '[&:hover>svg]:size-3.5'
  ]
})

const handleCancel = () => {
  open.value = false
}

const handleClear = () => {
  open.value = false
  emit('click:clear')
}

const handleOpenChange = (value: boolean) => {
  if (readonly) return
  open.value = value
}
</script>

<template>
  <DSPopover
    :open="readonly ? false : open"
    @update:open="handleOpenChange"
  >
    <PopoverAnchor as-child>
      <Button
        v-bind="$attrs"
        :variant
        :size
        :class="cn('relative group', props.class)"
      >
        <slot />
        <!--
        `bg-primary` reads against every variant's own fill except `primary`
        itself (indigo dot on indigo button) — pick `secondary`/`ghost`/
        `destructive` for a button that carries this indicator.
      -->
        <DSPopoverTrigger as-child>
          <span
            v-if="indicator"
            class="absolute rounded-full bg-primary size-2 -top-0.5 -right-0.5"
            :class="indicatorClasses"
            aria-hidden="true"
            @click.stop
          >
            <XIcon
              v-if="clearable && !readonly"
              class="size-2.5 text-primary-foreground transition-all"
              :class="open ? undefined : 'opacity-0 group-hover:opacity-100'"
              aria-hidden="true"
            />
          </span>
        </DSPopoverTrigger>
      </Button>
    </PopoverAnchor>
    <DSPopoverContent
      align="center" 
      class="p-5 w-48"
    >
      <p class="text-sm font-medium text-foreground mb-2 leading-tight text-center">
        Are you sure you want to clear this?
      </p>
      <div class="flex items-center gap-2">
        <DSButton
          variant="secondary"
          size="sm"
          class="w-full"
          @click="handleCancel"
        >
          Cancel
        </DSButton>
        <DSButton
          variant="destructive"
          size="sm"
          class="w-full"
          @click="handleClear"
        >
          Clear
        </DSButton>
      </div>
    </DSPopoverContent>
  </DSPopover>
</template>
